import React, { useState } from 'react';
import { CalendarClock, Check, X, HandCoins, Phone, Plus, RotateCcw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { crmService } from '@/lib/api';
import { fmtDateTime, toLocalInputValue } from './crmHelpers';

export const TaskRow = ({ task, onChanged, showDebtor = false, onOpenDebtor, highlighted = false }) => {
  const [rescheduling, setRescheduling] = useState(false);
  const [newAt, setNewAt] = useState(toLocalInputValue(new Date(task.scheduled_at)));
  const overdue = task.status === 'pending' && new Date(task.scheduled_at) < new Date();
  const Icon = task.type === 'promise' ? HandCoins : Phone;

  const update = async (data, msg) => {
    try { await crmService.updateTask(task.id, data); toast.success(msg); setRescheduling(false); onChanged(); } catch (e) { toast.error(e.response?.data?.detail || 'No se pudo actualizar'); }
  };

  return (
    <div id={`crm-task-${task.id}`} className={`rounded-lg border px-3 py-2 transition-shadow ${highlighted ? 'ring-2 ring-teal-500 shadow-lg' : ''} ${task.status !== 'pending' ? 'border-slate-100 bg-slate-50 opacity-70' : overdue ? 'border-red-200 bg-red-50/40' : 'border-slate-200 bg-white'}`} data-testid={`crm-task-${task.id}`}>
      <div className="flex items-start gap-2">
        <Icon className={`w-4 h-4 mt-0.5 ${task.type === 'promise' ? 'text-violet-600' : 'text-sky-600'}`} />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-slate-800">
            <span className={overdue ? 'text-red-600' : ''}>{fmtDateTime(task.scheduled_at)}</span>
            {showDebtor && task.debtor && (
              <> · <button type="button" onClick={() => onOpenDebtor?.(task.debtor_id)} className="text-teal-700 hover:underline" data-testid={`crm-task-open-${task.id}`}>{task.debtor.name}</button>
                {task.debtor.phone && <span className="text-slate-500 font-normal"> · {task.debtor.phone}</span>}</>
            )}
          </p>
          <p className="text-xs text-slate-500">{task.type === 'promise' ? 'Vencimiento de promesa' : 'Llamada'}{task.notes ? ` · ${task.notes}` : ''}{task.assigned_to_name ? ` · ${task.assigned_to_name}` : ''}</p>
          {task.status !== 'pending' && <p className="text-[11px] text-slate-400">{task.status === 'done' ? 'Realizada' : 'Cancelada'} {fmtDateTime(task.done_at)}</p>}
          {rescheduling && (
            <div className="flex gap-2 mt-2">
              <Input type="datetime-local" value={newAt} onChange={(e) => setNewAt(e.target.value)} className="h-8 text-xs" data-testid={`crm-task-reschedule-at-${task.id}`} />
              <Button size="sm" className="h-8" onClick={() => update({ scheduled_at: new Date(newAt).toISOString() }, 'Reprogramada')} data-testid={`crm-task-reschedule-save-${task.id}`}>Guardar</Button>
            </div>
          )}
        </div>
        {task.status === 'pending' && (
          <div className="flex items-center gap-1">
            <button type="button" title="Marcar realizada" onClick={() => update({ status: 'done' }, 'Marcada como realizada')} className="p-1 rounded text-emerald-600 hover:bg-emerald-50" data-testid={`crm-task-done-${task.id}`}><Check className="w-4 h-4" /></button>
            <button type="button" title="Reprogramar" onClick={() => setRescheduling(!rescheduling)} className="p-1 rounded text-sky-600 hover:bg-sky-50" data-testid={`crm-task-reschedule-${task.id}`}><RotateCcw className="w-4 h-4" /></button>
            <button type="button" title="Cancelar" onClick={() => update({ status: 'cancelled' }, 'Cancelada')} className="p-1 rounded text-slate-400 hover:bg-red-50 hover:text-red-600" data-testid={`crm-task-cancel-${task.id}`}><X className="w-4 h-4" /></button>
          </div>
        )}
      </div>
    </div>
  );
};

const CrmTasks = ({ debtorId, tasks, users, currentUserEmail, onChanged }) => {
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ scheduled_at: toLocalInputValue(new Date(Date.now() + 86400000)), notes: '', assigned_to: currentUserEmail || '' });

  const add = async () => {
    try {
      await crmService.createTask({ debtor_id: debtorId, scheduled_at: new Date(form.scheduled_at).toISOString(), notes: form.notes || null, assigned_to: form.assigned_to || null });
      toast.success('Llamada agendada'); setAdding(false); onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || 'No se pudo agendar'); }
  };

  const pending = tasks.filter((t) => t.status === 'pending');
  const past = tasks.filter((t) => t.status !== 'pending').slice(-10).reverse();
  return (
    <div className="space-y-3" data-testid="crm-tasks">
      <div className="flex items-center justify-between">
        <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Agenda del titular</p>
        <Button size="sm" variant="outline" className="h-8" onClick={() => setAdding(!adding)} data-testid="crm-task-add-toggle"><Plus className="w-4 h-4 mr-1" />Agendar llamada</Button>
      </div>
      {adding && (
        <div className="grid sm:grid-cols-4 gap-2 rounded-lg border border-sky-200 bg-sky-50/60 p-2">
          <Input type="datetime-local" value={form.scheduled_at} onChange={(e) => setForm({ ...form, scheduled_at: e.target.value })} className="h-8 text-xs bg-white" data-testid="crm-task-at" />
          <Select value={form.assigned_to} onValueChange={(v) => setForm({ ...form, assigned_to: v })}>
            <SelectTrigger className="h-8 text-xs bg-white" data-testid="crm-task-assignee"><SelectValue placeholder="Asignar a" /></SelectTrigger>
            <SelectContent>{users.map((u) => <SelectItem key={u.email} value={u.email}>{u.name}</SelectItem>)}</SelectContent>
          </Select>
          <Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Motivo" className="h-8 text-xs bg-white" data-testid="crm-task-notes" />
          <Button size="sm" className="h-8 bg-slate-900 hover:bg-slate-800" onClick={add} data-testid="crm-task-save"><CalendarClock className="w-4 h-4 mr-1" />Guardar</Button>
        </div>
      )}
      {pending.length ? pending.map((t) => <TaskRow key={t.id} task={t} onChanged={onChanged} />) : <p className="text-sm text-slate-400">Sin llamadas pendientes</p>}
      {past.length > 0 && (
        <details className="text-xs text-slate-500">
          <summary className="cursor-pointer">Historial ({past.length})</summary>
          <div className="space-y-2 mt-2">{past.map((t) => <TaskRow key={t.id} task={t} onChanged={onChanged} />)}</div>
        </details>
      )}
    </div>
  );
};

export default CrmTasks;
